import React, { useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { isDevAffordanceEnabled } from '../utils/devMode';
import { functions } from '../../firebase';
import { LightningBoltIcon } from '@heroicons/react/outline';

/**
 * Environment check for showing dev tools (auto-complete buttons).
 * Only visible in local development only.
 */
const isDev = isDevAffordanceEnabled();

/**
 * AutoCompleteButton
 * ------------------
 * Dev-only button to auto-complete a form with demo data.
 * 
 * This component is used in two contexts:
 * 
 * 1. Provider Mode (selfComplete=false):
 *    - Used by RMS Clinicians on the provider patient overview page
 *    - Can specify any role to complete forms for either respondent
 *    - Requires `role` prop to be explicitly provided
 * 
 * 2. Self-Complete Mode (selfComplete=true):
 *    - Used by service users (school users / guardians) on their overview page
 *    - Role is derived from the user's custom claims on the backend
 *    - Users can only complete their own forms (not the other respondent's)
 * 
 * Features:
 * - Only visible in local development only
 * - Can be re-clicked even on completed forms to trigger recalculation
 *   (e.g., for SNAP-IV scoring or risk stratification)
 * - Shows loading state during API call
 * - Provides visual feedback based on form completion status
 * 
 * Props:
 * @param {string} formKey - The form key to auto-complete (e.g., 'waysOfConnecting')
 * @param {string} role - 'school_user' | 'guardian' - Only used when selfComplete=false
 * @param {string} orgId - Organisation ID
 * @param {string} patientRecordId - Patient record ID
 * @param {boolean} selfComplete - If true, uses self-complete mode (derives role from claims)
 * @param {function} onComplete - Callback after successful completion: (formKey, role, resultData) => void
 * @param {boolean} isComplete - Whether the form is already completed (affects styling)
 * @param {string} className - Additional CSS classes for the button
 * @param {string} size - Button size: 'sm' (default) | 'md'
 */
const AutoCompleteButton = ({ 
  formKey, 
  role, 
  orgId, 
  patientRecordId, 
  selfComplete = false,
  onComplete, 
  isComplete = false,
  className = '',
  size = 'sm'
}) => {
  const [loading, setLoading] = useState(false);

  const handleAutoComplete = async (e) => {
    e.stopPropagation(); // Prevent any parent click handlers
    e.preventDefault();
    if (loading) return;

    setLoading(true);
    try {
      const autoCompleteForm = httpsCallable(functions, 'rmsAutoCompleteForm');
      
      // Build request payload based on mode
      const payload = {
        orgId,
        patientRecordId,
        formKey,
      };
      
      if (selfComplete) {
        // Self-complete mode: backend derives role from user's claims
        payload.selfComplete = true;
      } else {
        // Provider mode: explicitly specify the role
        payload.role = role;
      }
      
      const result = await autoCompleteForm(payload);

      if (result.data?.success) {
        console.log(`[AutoComplete] Successfully completed ${formKey}${selfComplete ? ' (self-complete)' : ` for ${role}`}:`, result.data);
        // Log if risk was calculated
        if (result.data.riskCalculated) {
          console.log(`[AutoComplete] Risk stratification calculated! Tier: ${result.data.riskTier}`);
        }
        // Log if SNAP-IV was scored
        if (result.data.scaleResults) {
          console.log(`[AutoComplete] SNAP-IV scored: totalScore=${result.data.scaleResults.totalScore}`);
        }
        if (onComplete) {
          // For self-complete, we don't have the role on the frontend, so pass null
          // The callback can use the result data if it needs the role
          onComplete(formKey, selfComplete ? null : role, result.data);
        }
      } else {
        console.error('[AutoComplete] Failed:', result.data);
      }
    } catch (err) {
      console.error('[AutoComplete] Error:', err);
      // Show error but don't block - form data is auto-refreshed via snapshot
    } finally {
      setLoading(false);
    }
  };

  // Only render in local development only
  if (!isDev) return null;

  // Size-based classes
  const sizeClasses = size === 'md' 
    ? 'p-1.5' 
    : 'p-1';
  const iconClasses = size === 'md'
    ? 'w-5 h-5'
    : 'w-4 h-4';

  // Build tooltip text
  const tooltipText = isComplete 
    ? 'Re-run auto-complete (triggers recalculation)' 
    : 'Auto-complete with demo data';

  return (
    <button
      type="button"
      onClick={handleAutoComplete}
      disabled={loading}
      title={tooltipText}
      className={`rounded transition-colors ${sizeClasses} ${
        loading
          ? 'text-amber-500 animate-pulse cursor-wait'
          : isComplete
          ? 'text-green-500 hover:text-green-700 hover:bg-green-50'
          : 'text-primary hover:text-primary/80 hover:bg-primary/5'
      } ${className}`}
    >
      <LightningBoltIcon className={iconClasses} />
    </button>
  );
};

export default AutoCompleteButton;
